<template>
  <x-form
    :loading="introductionState.state !== 'success'"
    :loading-title="$t('Stiamo caricando le domande e le tue risposte precedenti')"
    :submit-text="$t('Invia')"
    @submit="submit"
    style="max-width: 80ch"
  >
    <template v-if="kind === 'survey'">
      <p>{{ $t('Benvenuti nel nostro questionario annuale obbligatorio per i partner!') }}</p>
      <p>
        {{
          $t(
            'Questo strumento è pensato per farci comprendere al meglio le vostre necessità e aspettative, nonché per assicurarci che i dati che ci fornite siano sempre aggiornati.',
          )
        }}
      </p>
      <p>
        {{
          $t(
            "Raccogliendo informazioni dettagliate sulle vostre competenze e caratteristiche, possiamo indirizzare in modo più accurato i lavori verso i partner più adatti, migliorando l'efficienza e la soddisfazione di tutti.",
          )
        }}
      </p>
      <p>
        {{
          $t(
            "Vi chiederemo di dedicare un momento per compilare questo questionario una volta all'anno, garantendo così l'attualità e la precisione delle informazioni condivise. La vostra partecipazione è essenziale per rafforzare la nostra collaborazione e guidarci verso un futuro di successo congiunto.",
          )
        }}
      </p>
      <p>{{ $t('Grazie per il vostro impegno e la vostra collaborazione.') }}</p>
    </template>

    <template v-if="kind === 'introduction'">
      <form-group :name="$t('Presentazione')" :helper-text="$t('Descrivi brevemente la tua aziende e i prodotti e servizi che fornisce')">
        <pf-textarea
          :model-value="introduction.presentazione"
          @update:model-value="introduction.presentazione = String($event)"
          :required="required"
          name="presentazione"
          rows="5"
        />
      </form-group>

      <form-group
        :name="$t('Logo')"
        :helper-text="$t('Verrà mostrato nella pagina di registrazione quando gli utenti utilizzeranno il tuo link di affiliazione')"
      >
        <div v-if="introduction.has_logo">
          <img :src="`/rest/company_logo/${login.id_azienda}?size=64`" />

          <pf-button variant="link" @click="deleteLogo"> <xmark-icon />{{ $t('Elimina logo') }} </pf-button>
        </div>

        <input type="file" ref="inputLogoRef" name="logo" @change="getLogoAttachment" style="margin-top: 10px" />
      </form-group>

      <form-group :name="$t('Brochure')">
        <div v-if="introduction.has_brochure">
          <a :href="`/rest/company_brochure/${login.id_azienda}`"> <file-arrow-down-icon />{{ $t('Brochure') }} </a>
          <pf-button variant="link" @click="deleteBrochure"> <xmark-icon />{{ $t('Elimina brochure') }} </pf-button>
        </div>

        <input type="file" ref="inputBrochureRef" name="brochure" @change="getBrochureAttachment" style="margin-top: 10px" />
      </form-group>
    </template>

    <form-group :name="_t('Categoria')" :required="required">
      <pf-form-select
        name="question"
        :required="required"
        :model-value="introduction['categoria_prospect']"
        @update:model-value="introduction['categoria_prospect'] = $as<(typeof CRM_CATEGORIAPROSPECT)[number]>($event)"
      >
        <pf-form-select-option v-for="option in CRM_CATEGORIAPROSPECT" :key="option">
          {{ option }}
        </pf-form-select-option>
      </pf-form-select>
    </form-group>

    <form-group :name="_t('Numero dipendenti')" :required="required">
      <pf-form-select
        name="question"
        :required="required"
        :model-value="introduction['dipendenti']"
        @update:model-value="introduction['dipendenti'] = $as<(typeof CRM_DIPENDENTI)[number]>($event)"
      >
        <pf-form-select-option v-for="option in CRM_DIPENDENTI" :key="option">
          {{ option }}
        </pf-form-select-option>
      </pf-form-select>
    </form-group>

    <form-group :name="_t('Anni di attività')" :required="required">
      <pf-form-select
        name="question"
        :required="required"
        :model-value="introduction['anni_attivita']"
        @update:model-value="introduction['anni_attivita'] = $as<(typeof CRM_ANNIATTIVITA)[number]>($event)"
      >
        <pf-form-select-option v-for="option in CRM_ANNIATTIVITA" :key="option">
          {{ option }}
        </pf-form-select-option>
      </pf-form-select>
    </form-group>

    <form-group :name="_t('Fatturato attuale')" :required="required">
      <pf-form-select
        name="question"
        :required="required"
        :model-value="introduction['fatturato']"
        @update:model-value="introduction['fatturato'] = $as<(typeof CRM_FATTURATO)[number]>($event)"
      >
        <pf-form-select-option v-for="option in CRM_FATTURATO" :key="option">
          {{ option }}
        </pf-form-select-option>
      </pf-form-select>
    </form-group>

    <form-group :name="_t('Clienti attuali')" :required="required">
      <pf-form-select
        name="question"
        :required="required"
        :model-value="introduction['clienti']"
        @update:model-value="introduction['clienti'] = $as<(typeof CRM_CLIENTI)[number]>($event)"
      >
        <pf-form-select-option v-for="option in CRM_CLIENTI" :key="option">
          {{ option }}
        </pf-form-select-option>
      </pf-form-select>
    </form-group>

    <form-group :name="_t('Clienti preferiti')" :required="required">
      <pf-form-select
        name="question"
        :required="required"
        multiple
        :model-value="introduction['dimensione_clienti']"
        @update:model-value="introduction['dimensione_clienti'] = $as<Writable<typeof CRM_DIMENSIONECLIENTI>>($event)"
      >
        <pf-form-select-option v-for="option in CRM_DIMENSIONECLIENTI" :key="option">
          {{ option }}
        </pf-form-select-option>
      </pf-form-select>
    </form-group>

    <form-group :name="_t('Associazioni di cui fate parte')">
      <pf-form-select
        name="question"
        multiple
        :model-value="introduction['associazioni']"
        @update:model-value="introduction['associazioni'] = $as<Writable<typeof CRM_ASSOCIAZIONI>>($event)"
      >
        <pf-form-select-option v-for="option in CRM_ASSOCIAZIONI" :key="option">
          {{ option }}
        </pf-form-select-option>
      </pf-form-select>
    </form-group>

    <form-group :name="_t('Preferenza di fatturazione')" :required="required">
      <pf-form-select
        name="question"
        :required="required"
        :model-value="introduction['preferenza_fatturazione']"
        @update:model-value="introduction['preferenza_fatturazione'] = $as<(typeof CRM_PREFERENZAFATTURAZIONE)[number]>($event)"
      >
        <pf-form-select-option v-for="option in CRM_PREFERENZAFATTURAZIONE" :key="option">
          {{ option }}
        </pf-form-select-option>
      </pf-form-select>
    </form-group>

    <form-group
      v-if="kind === 'introduction'"
      :name="$t('Vuoi ricevere segnalazioni relative ad opportunità commerciali che arrivano dai nostri clienti?')"
      :helper-text="$t('Ti segnalaremo ogni richiesta da parte di nostri clienti relativa a progetti nel tuo settore e nella tua zona.')"
    >
      <pf-switch
        v-model:checked="introduction.receive_opportunity"
        name="receive_opportunity"
        :value="true"
        off-value="0"
        :label="introduction.receive_opportunity ? $t('Sì') : $t('No')"
      />
    </form-group>

    <template v-if="kind === 'survey'">
      <form-group :name="$t('Cosa ti piace dell\'area clienti?')" required>
        <pf-textarea v-model="introduction.admin_pros" name="admin_pros" :required="required" />
      </form-group>

      <form-group :name="$t('Cosa non ti piace dell\'area clienti?')" required>
        <pf-textarea v-model="introduction.admin_cons" name="admin_cons" :required="required" />
      </form-group>

      <form-group :name="$t('Quali nuove funzionalità vorresti vedere implementate in quest\'area clienti?')" required>
        <pf-textarea v-model="introduction.admin_feature_request" name="admin_feature_request" :required="required" />
      </form-group>
    </template>
  </x-form>
</template>

<style scoped>
label {
  display: blocK;
}
:deep(.form-group) {
  max-width: 80ch;
}
textarea {
  width: 100%;
  height: 100px;
}
</style>

<script lang="ts" setup>
import { computed, useTemplateRef } from 'vue';
import { $t as _t } from '@/i18n';
import {
  CRM_ANNIATTIVITA,
  CRM_ASSOCIAZIONI,
  CRM_CATEGORIAPROSPECT,
  CRM_CLIENTI,
  CRM_DIMENSIONECLIENTI,
  CRM_DIPENDENTI,
  CRM_FATTURATO,
  CRM_PREFERENZAFATTURAZIONE,
} from '../common/consts';
import { asyncComputed } from '@common/asyncComputed';
import { CompanyBrochureResource, CompanyLogoResource, IntroductionResource } from '@/resources';
import { useLoginStore } from '../store/login';
import FileArrowDownIcon from '@vue-patternfly/icons/file-arrow-down-icon';
import XmarkIcon from '@vue-patternfly/icons/xmark-icon';
import { useFunctionRunningState } from '@common/use';
import type { ComponentProps } from 'vue-component-type-helpers';
import type XForm from './x-form.vue';

type XFormProps = ComponentProps<typeof XForm>;

type Props = {
  kind?: 'survey' | 'introduction';
} & /* @vue-ignore */ Omit<XFormProps, 'loading' | 'loadingTitle' | 'submitText' | 'onSubmit'>;

const login = useLoginStore();
const props = withDefaults(defineProps<Props>(), {
  kind: 'introduction',
});

const emit = defineEmits<{
  (e: 'submit'): void;
}>();

const inputLogo = useTemplateRef('inputLogoRef');
const inputBrochure = useTemplateRef('inputBrochureRef');

const { result: introduction, state: introductionState } = asyncComputed(async () => {
  return await new IntroductionResource().get(1);
});

const required = computed(() => props.kind === 'survey');

const { run: submit } = useFunctionRunningState(
  async () => {
    await new IntroductionResource().save(introduction.value);
    emit('submit');
    introductionState.update();
  },
  { preventMultiple: true },
);

function getLogoAttachment() {
  if (!inputLogo?.value?.files) {
    return;
  }
  introduction.value.logo = inputLogo.value.files[0] ?? null;
}

async function deleteLogo() {
  if (!confirm('Vuoi procedere con la cancellazione del logo?')) {
    return;
  }
  new CompanyLogoResource().delete(1);
  introductionState.update();
}

function getBrochureAttachment() {
  if (!inputBrochure?.value?.files) {
    return;
  }
  introduction.value.brochure = inputBrochure.value.files[0] ?? null;
}

async function deleteBrochure() {
  if (!confirm('Vuoi procedere con la cancellazione del brochure?')) {
    return;
  }
  new CompanyBrochureResource().delete(1);
  introductionState.update();
}
</script>
